'use client';

import { useEffect, useRef } from 'react';

const BROWSER_BACK_ENTRY_KEY = '__browser_back_close_entry__';

const activeEntryIds = new Set<string>();

const getEntryId = (state: unknown): string | undefined =>
  (state as Record<string, string> | null)?.[BROWSER_BACK_ENTRY_KEY];

const releaseEntryAfterPopState = (entryId: string) => {
  window.setTimeout(() => activeEntryIds.delete(entryId), 0);
};

export const isBrowserBackCloseTraversal = (state: unknown) =>
  activeEntryIds.size > 0 || Boolean(getEntryId(state));

const useCloseOnBrowserBack = (
  onBack: () => void,
  enabled = true,
  onSettled?: () => void
) => {
  const onBackRef = useRef(onBack);
  const onSettledRef = useRef(onSettled);

  useEffect(() => {
    onBackRef.current = onBack;
    onSettledRef.current = onSettled;
  }, [onBack, onSettled]);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      return;
    }

    const entryId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const { previous_url: previousUrl, current_url: currentUrl } = window;
    let isPushed = false;
    let isPopped = false;

    const restoreAnalyticsUrls = () => {
      window.previous_url = previousUrl;
      window.current_url = currentUrl;
    };

    const reassertEntry = () => {
      if (getEntryId(window.history.state) !== entryId) {
        return;
      }

      window.history.replaceState(window.history.state, '');
      restoreAnalyticsUrls();
    };

    const removeReassertListeners = () => {
      document.removeEventListener('pointerdown', reassertEntry, true);
      document.removeEventListener('keydown', reassertEntry, true);
      window.removeEventListener('blur', reassertEntry);
    };

    const pushEntry = () => {
      isPushed = true;
      activeEntryIds.add(entryId);
      window.history.pushState(
        {
          ...(window.history.state || {}),
          [BROWSER_BACK_ENTRY_KEY]: entryId,
        },
        ''
      );
      restoreAnalyticsUrls();
      window.addEventListener('popstate', handlePopState);
      document.addEventListener('pointerdown', reassertEntry, {
        capture: true,
        once: true,
      });
      document.addEventListener('keydown', reassertEntry, {
        capture: true,
        once: true,
      });
      window.addEventListener('blur', reassertEntry, { once: true });
    };

    const pushEntryWhenVisible = () => {
      if (document.visibilityState === 'hidden') {
        return;
      }

      document.removeEventListener('visibilitychange', pushEntryWhenVisible);
      pushEntry();
    };

    const handlePopState = (event: PopStateEvent) => {
      const landedEntryId = getEntryId(event.state);
      const landedOnOtherActiveEntry =
        landedEntryId !== undefined &&
        landedEntryId !== entryId &&
        activeEntryIds.has(landedEntryId);

      if (landedOnOtherActiveEntry) {
        return;
      }

      isPopped = landedEntryId !== entryId;
      window.removeEventListener('popstate', handlePopState);
      if (isPopped) {
        releaseEntryAfterPopState(entryId);
      }
      restoreAnalyticsUrls();
      onBackRef.current();
    };

    if (document.visibilityState === 'hidden') {
      document.addEventListener('visibilitychange', pushEntryWhenVisible);
    } else {
      pushEntry();
    }

    return () => {
      document.removeEventListener('visibilitychange', pushEntryWhenVisible);
      removeReassertListeners();
      window.removeEventListener('popstate', handlePopState);

      if (!isPushed || isPopped) {
        onSettledRef.current?.();
        return;
      }

      if (getEntryId(window.history.state) !== entryId) {
        releaseEntryAfterPopState(entryId);
        onSettledRef.current?.();
        return;
      }

      const handleEntryRemoved = () => {
        window.removeEventListener('popstate', handleEntryRemoved);
        releaseEntryAfterPopState(entryId);
        restoreAnalyticsUrls();
        onSettledRef.current?.();
      };

      window.addEventListener('popstate', handleEntryRemoved);
      window.history.back();
    };
  }, [enabled]);
};

export default useCloseOnBrowserBack;
